import React from "react";
import { useTranslation } from "react-i18next";

import {
  Dolar,
  AirPayout,
  AirLuggage,
  VegetarianFood,
  TechnicalSupport,
  PriceTag,
  LuggageSolid,
  ESim as ESimIcon,
} from "@airhelp/icons";
import { orderBy } from "lodash";

import { Benefits } from "models/userPolicy/Benefits";
import { ESim } from "models/userPolicy/ESim";
import { pluralVersion } from "utils/translations";

import PlanDetailsList from "../PlanDetailsList";
import { PlanItem } from "../PlanDetailsList/PlanDetailsList";
import Section from "../Section";

interface IComponent {
  benefits: Benefits;
  eSim: ESim | null;
}

const YourPlanBenefits: React.FC<IComponent> = ({ benefits, eSim }) => {
  const { t } = useTranslation();

  const airPayoutTokens = benefits.airPayoutTokens.toString();
  const airPayoutPayout = `€${benefits.airPayoutPayout.toString()}`;
  const airLuggagePayout = `€${benefits.airLuggagePayout.toString()}`;
  const loungeAccessIncluded = benefits.loungeAccessEnabled || benefits.journeyLoungeAccessEnabled;

  const eSimDiscount = eSim?.discount.toString() || "";
  const airLuggageTip = benefits.airLuggageMaxBags === 1 ? "air_luggage_tip" : "air_luggage_desc";

  const benefitsList: PlanItem[] = [
    {
      title_tkey: pluralVersion(
        "my_account.my_benefits.your_benefits.list.air_payout",
        benefits.airPayoutTokens,
        benefits.airPayoutUnlimited
      ),
      value: { credits: airPayoutTokens },
      icon: AirPayout,
      tooltip: {
        content: `my_account.my_benefits.your_benefits.list.air_payout_${
          benefits.airPayoutUnlimited ? "complete" : "essential"
        }_desc`,
        value: { credits: airPayoutTokens, amount: airPayoutPayout },
      },
      key: "air_payout",
      included: !!benefits.airPayoutIncluded,
    },
    {
      title_tkey: pluralVersion(
        "my_account.my_benefits.your_benefits.list.air_luggage",
        benefits.airLuggageTokens,
        benefits.airLuggageUnlimited
      ),
      value: { credits: benefits.airLuggageTokens.toString() },
      icon: AirLuggage,
      tooltip: {
        content: `my_account.my_benefits.your_benefits.list.${airLuggageTip}`,
        value: {
          amount: airLuggagePayout,
          bags: benefits.airLuggageMaxBags,
        },
      },
      key: "air_luggage",
      included: !!benefits.airLuggageIncluded,
    },
    {
      title_tkey: "my_account.my_benefits.your_benefits.list.airport_lounge_access",
      icon: VegetarianFood,
      tooltip: {
        content: "my_account.my_benefits.your_benefits.list.airport_lounge_access_desc",
      },
      key: "airport_lounge_access",
      included: loungeAccessIncluded,
    },
    {
      title_tkey: "my_account.my_benefits.your_benefits.list.compensation_claims",
      value: { compensation: "€600" },
      icon: Dolar,
      tooltip: {
        content: "my_account.my_benefits.your_benefits.list.compensation_claims_desc",
        value: { compensation: "€600" },
      },
      key: "compensation_claims",
      included: true,
    },
    {
      title_tkey: "my_account.my_benefits.your_benefits.list.expenses_claims",
      value: { compensation: "€6,000" },
      icon: PriceTag,
      tooltip: {
        content: "my_account.my_benefits.your_benefits.list.expenses_claims_desc",
        value: { compensation: "€6,000" },
      },
      key: "expenses_claims",
      included: true,
    },
    {
      title_tkey: "my_account.my_benefits.your_benefits.list.luggage_claims",
      value: { compensation: "€1,400" },
      icon: LuggageSolid,
      tooltip: {
        content: "my_account.my_benefits.your_benefits.list.luggage_claims_desc",
        value: { compensation: "€1,400" },
      },
      key: "luggage_claims",
      included: true,
    },
    {
      title_tkey: "my_account.my_benefits.your_benefits.list.esim",
      value: { discount: eSimDiscount },
      icon: ESimIcon,
      tooltip: {
        content: "my_account.my_benefits.your_benefits.list.esim_desc",
      },
      key: "esim",
      included: !!eSim,
    },
    {
      title_tkey: "my_account.my_benefits.your_benefits.list.live_chat_support",
      icon: TechnicalSupport,
      tooltip: {
        content: "my_account.my_benefits.your_benefits.list.live_chat_support_desc",
      },
      key: "live_support",
      included: true,
    },
  ];

  const sortedBenefits = orderBy(benefitsList, ["included"], ["desc"]);

  return (
    <Section
      header={t("my_account.my_benefits.your_benefits.header")}
      description={t("my_account.my_benefits.your_benefits.description")}
    >
      <PlanDetailsList planDetails={sortedBenefits} />
    </Section>
  );
};

export default YourPlanBenefits;
